'use client';

import { DealerStore } from '../../dealerStoreType';
import { useExpander } from '../../../../hooks/useExpander';
import expanderStyles from '../../../common/expander/styles.module.scss';
import { CatRadioField } from '@blocks-react/components/RadioField';
import { DealerLocatorSearchResultAddress } from '../dealer-locator-search-result-address-information';
import { DealerLocatorSearchResultBranchName } from '../dealer-locator-search-result-branch-name';
import { DealerLocatorSearchResultDistance } from '../dealer-locator-search-result-distance';
import { DealerLocatorSearchResultName } from '../dealer-locator-search-result-name';
import { DealerLocatorNonInstantAccessWarning } from '../dealer-locator-non-instant-access-warning';
import { useFormContext } from 'react-hook-form';
import { useRadioController } from '../../../../hooks/form-controllers/useRadioFieldController';
import { Conditional } from '../../../common/conditional';
import { Expander } from '../../../common/expander';
import { SELECTED_STORE_ID } from '../../constants';
import { useEffect, useRef } from 'react';
import { DealerLocatorSearchResultMobileContact } from '../dealer-locator-search-result-mobile-contact/DealerLocatorSearchResultMobileContact';
import { CatBadge } from '@blocks-react/components/Badge';
import cx from 'classnames';
import styles from '../dealer-locator-search-result-distance/DealerLocatorSearchResultDistance.module.scss';
import { useBreakpoint, useDrawer } from '../../../../hooks';
import { PAGE_BREAKPOINTS } from '../../../../constants/commonConstants';
import { CatButton } from '@blocks-react/components/Button';
interface DealerLocatorSearchResultProps {
  children?: React.ReactNode;
  store: DealerStore;
  unitOfMeasure?: 'mi' | 'km';
  closestLabel: string;
  setScrollToStore?: (arg1: boolean) => void;
  isDisabled?: boolean;
  showNonInstantAccessWarning?: boolean;
  isMapView?: boolean;
  warningMessage?: string;
  showAvailabilityBTNAndMsg?: boolean;
  availabilityBtnAndMsg?: React.ReactNode;
  isStoreLocationListViewUpdateFlag?: boolean;
  isStoreLocationMapViewUpdateFlag?: boolean;
  storeDetailsLabel?: string;
  storeDetailsDrawer?: ReturnType<typeof useDrawer>;
  isStoreLocationOptimizationFlag?: boolean;
  setselectedStoreObjDrawer?: (arg0: DealerStore) => void;
  forStoreDetailsDrawer?: boolean;
}

export const DealerLocatorSearchResult = ({
  children,
  store,
  unitOfMeasure,
  closestLabel,
  setScrollToStore,
  isDisabled,
  showNonInstantAccessWarning = false,
  warningMessage = '',
  showAvailabilityBTNAndMsg = false,
  availabilityBtnAndMsg,
  isStoreLocationListViewUpdateFlag = false,
  isStoreLocationMapViewUpdateFlag = false,
  isStoreLocationOptimizationFlag = false,
  isMapView = false,
  storeDetailsDrawer,
  storeDetailsLabel,
  setselectedStoreObjDrawer,
  forStoreDetailsDrawer
}: DealerLocatorSearchResultProps) => {
  const { isExpanded, toggle } = useExpander();
  const onExpandHandler = () => toggle(!isDisabled && !isExpanded);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const { setValue, watch } = useFormContext();
  const { getRadioField } = useRadioController({ name: SELECTED_STORE_ID });
  const selectedStoreRef = useRef<HTMLDivElement | null>(null);
  const selectedStoreId: string = watch(SELECTED_STORE_ID);

  const unitOfMeasureLabel = `${unitOfMeasure ? ' ' : ''}${
    unitOfMeasure || ''
  }`;
  const distanceLabel = `${Number(store.geo.distance).toFixed(
    1
  )}${unitOfMeasureLabel}`;
  const address = `${store.address.line1 || ''}${
    store.address.line1 ? ',' : ''
  } ${store.address.state || ''} ${store.address.zipCode || ''}`;
  const isSelected = store.id.toString() === selectedStoreId;

  useEffect(() => {
    if (!isSelected) {
      selectedStoreRef.current = null;
    }
  }, [isSelected]);

  //scrolls to selected store when the user returns from map view and setScrollToStore is defined
  useEffect(() => {
    if (setScrollToStore && selectedStoreRef.current !== null) {
      setTimeout(() => {
        selectedStoreRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end'
        });
        setScrollToStore(false);
      }, 100);
    }
    toggle(false);
  }, [setScrollToStore, toggle]);

  const handleSelect = () => {
    setValue(SELECTED_STORE_ID, store.id.toString());
  };

  useEffect(() => {
    if (isExpanded) {
      onExpandHandler();
    }
  }, [store]);

  const distanceIsValid =
    (store.geo.distance || store.geo.distance === 0) &&
    !isNaN(store.geo.distance);
  const onClickStoreDetails = () => {
    setValue(SELECTED_STORE_ID, store.id.toString());
    storeDetailsDrawer?.openDrawer();
    setselectedStoreObjDrawer?.(store);
  };
  const dealerLocatorSearchResultCard = () => {
    return (
      <>
        <div className="d-flex justify-content-between">
          <div>
            <CatRadioField
              {...getRadioField(SELECTED_STORE_ID, store.id.toString())}
              onBlRadioChange={handleSelect}
              className="mt-0.5"
              disabled={isDisabled}
            >
              <DealerLocatorSearchResultName
                isNonInstantAccessDealer={showNonInstantAccessWarning}
              >
                {store.dealerName}
              </DealerLocatorSearchResultName>
            </CatRadioField>
            <DealerLocatorSearchResultBranchName>
              {store.name}
            </DealerLocatorSearchResultBranchName>
            <DealerLocatorSearchResultAddress>
              {address}
            </DealerLocatorSearchResultAddress>

            <Conditional test={showNonInstantAccessWarning}>
              <DealerLocatorNonInstantAccessWarning>
                {warningMessage}
              </DealerLocatorNonInstantAccessWarning>
            </Conditional>
          </div>
          <DealerLocatorSearchResultDistance>
            {store.geo.isClosest && <p className="mb-0">{closestLabel}</p>}
            {distanceIsValid ? (
              <p className="text-nowrap">{distanceLabel}</p>
            ) : null}
          </DealerLocatorSearchResultDistance>
        </div>
        <Expander isExpanded={isExpanded} onExpandHandler={onExpandHandler} />
        <Conditional test={!!showAvailabilityBTNAndMsg}>
          {availabilityBtnAndMsg}
        </Conditional>
      </>
    );
  };
  return (
    <div
      className={
        !forStoreDetailsDrawer ? expanderStyles['expander-container'] : ''
      }
      data-testid="search-result-component"
      ref={isSelected ? selectedStoreRef : null}
    >
      <Conditional
        test={!(isStoreLocationListViewUpdateFlag && !isMD)}
        fallback={
          <div
            className={cx({
              [' py-3 px-1']: !forStoreDetailsDrawer
            })}
          >
            <div className="d-flex align-items-start align-self-stretch">
              {!forStoreDetailsDrawer && (
                <Conditional
                  test={
                    !(isMapView && isStoreLocationMapViewUpdateFlag && !isMD)
                  }
                  fallback={
                    <div className="d-none">
                      <CatRadioField
                        {...getRadioField(
                          SELECTED_STORE_ID,
                          store.id.toString()
                        )}
                        onBlRadioChange={handleSelect}
                        className="mt-0.5"
                        disabled={isDisabled}
                      ></CatRadioField>
                    </div>
                  }
                >
                  <CatRadioField
                    {...getRadioField(SELECTED_STORE_ID, store.id.toString())}
                    onBlRadioChange={handleSelect}
                    className="mt-0.5"
                    disabled={isDisabled}
                  ></CatRadioField>
                </Conditional>
              )}
              <div
                className={cx('d-flex align-items-start', {
                  ['w-100']: isStoreLocationListViewUpdateFlag
                })}
              >
                <div className="d-flex flex-column align-items-start">
                  <DealerLocatorSearchResultName
                    isNonInstantAccessDealer={showNonInstantAccessWarning}
                    isStoreLocationListViewUpdateFlag={
                      isStoreLocationListViewUpdateFlag
                    }
                    isStoreLocationMapViewUpdateFlag={
                      isStoreLocationMapViewUpdateFlag
                    }
                  >
                    {store.dealerName}
                  </DealerLocatorSearchResultName>
                  <DealerLocatorSearchResultBranchName
                    isStoreLocationListViewUpdateFlag={
                      isStoreLocationListViewUpdateFlag
                    }
                    isStoreLocationMapViewUpdateFlag={
                      isStoreLocationMapViewUpdateFlag
                    }
                  >
                    {store.name}
                  </DealerLocatorSearchResultBranchName>
                  <DealerLocatorSearchResultAddress
                    isStoreLocationMapViewUpdateFlag={
                      isStoreLocationMapViewUpdateFlag && !isMD
                    }
                  >
                    {address}
                  </DealerLocatorSearchResultAddress>
                  <DealerLocatorSearchResultMobileContact
                    showPhoneIcon={
                      isMapView &&
                      isStoreLocationMapViewUpdateFlag &&
                      !isMD &&
                      !!store?.address?.phone
                    }
                  >
                    {store.address.phone}
                  </DealerLocatorSearchResultMobileContact>
                  <Conditional test={showNonInstantAccessWarning}>
                    <DealerLocatorNonInstantAccessWarning>
                      {warningMessage}
                    </DealerLocatorNonInstantAccessWarning>
                  </Conditional>
                </div>

                <div
                  className={cx(
                    'd-flex flex-column align-items-end justify-content-between',
                    {
                      [styles['spacingElement']]:
                        isStoreLocationListViewUpdateFlag
                    }
                  )}
                >
                  {!forStoreDetailsDrawer && (
                    <Conditional
                      test={isStoreLocationOptimizationFlag}
                      fallback={<div></div>}
                    >
                      <CatButton
                        variant="inline"
                        onClick={onClickStoreDetails}
                        className="text-nowrap"
                      >
                        {storeDetailsLabel}
                      </CatButton>
                    </Conditional>
                  )}
                  <DealerLocatorSearchResultDistance>
                    {store.geo.isClosest && (
                      <Conditional
                        test={isStoreLocationListViewUpdateFlag}
                        fallback={<p className="mb-0">{closestLabel}</p>}
                      >
                        <div className="ms-auto">
                          <CatBadge text={closestLabel} />
                        </div>
                      </Conditional>
                    )}
                    {distanceIsValid ? (
                      <p
                        className={cx('text-nowrap', {
                          [styles['distanceText']]:
                            isStoreLocationListViewUpdateFlag
                        })}
                      >
                        {distanceLabel}
                      </p>
                    ) : null}
                  </DealerLocatorSearchResultDistance>
                </div>
              </div>
            </div>
            <Conditional test={!!showAvailabilityBTNAndMsg}>
              {availabilityBtnAndMsg}
            </Conditional>
          </div>
        }
      >
        <div className={`${expanderStyles['expander-header']} py-3 px-1`}>
          {dealerLocatorSearchResultCard()}
        </div>
      </Conditional>

      <Conditional test={isExpanded}>{children}</Conditional>
    </div>
  );
};
